<template>
    <div @click.stop="viewItem" class="bg-white transition-all cursor-pointer flex flex-col"
        :class="isModal ? '' : 'border-b-4 sm:border border-gray-200 sm:rounded-2xl shadow-sm hover:shadow-md sm:hover:border-blue-400'">
        <div class="relative w-full flex flex-col" :class="isModal ? 'p-3' : 'p-4 sm:p-5 md:p-6'">
            <!-- 👤 User Info Section -->
            <div @click.stop="goToProfile" :title="`Click to view ${item.user.name}'s profile`"
                class="flex items-start justify-between gap-4 mb-4 cursor-pointer group">
                <div class="flex items-center gap-3">
                    <img :src="profilePicture" alt="User Avatar"
                        class="w-8 sm:w-10 h-8 sm:h-10 rounded-full object-cover border" />
                    <div>
                        <h4
                            class="text-sm sm:text-base font-semibold text-gray-800 group-hover:text-blue-600 transition-colors">
                            {{ item.user.name }}
                        </h4>
                        <p class="text-xs text-gray-400 group-hover:text-blue-600 transition-colors">
                            {{ postedAt }}
                        </p>
                    </div>
                </div>
                <p v-if="!isModal" class="hidden sm:flex text-xs text-gray-400">
                    #{{ paginatorInfo.firstItem + index }}
                </p>
            </div>

            <!-- 📝 Post Title -->
            <h3 class="text-sm sm:text-lg font-light sm:font-semibold sm:text-blue-900 line-clamp-2 sm:line-clamp-3 mb-3 break-all"
                :class="isModal ? '' : 'truncate-3'">
                {{ item.short_description }}
            </h3>

            <h3 v-if="isModal" class="text-sm sm:text-base text-gray-600 line-clamp-2 sm:line-clamp-3 mb-3 break-all">
                {{ item.description }}
            </h3>

            <!-- 💰 Budget Info -->
            <div class="text-xs sm:text-sm text-gray-600 space-y-1">
                <p>
                    <strong class="mr-2">Budget Required:</strong>
                    {{ item.budget_required ? 'Yes' : 'No' }}
                </p>
                <p v-if="item.budget_required" class="flex items-center flex-wrap">
                    <strong class="mr-2">Estimated Amount:</strong>
                    <img class="h-3" src="../../../assets/images/default/DirhamBlack.svg" alt="AED" />
                    <span class="ml-1">{{ item.estimated_amount }}</span>
                </p>
            </div>

            <!-- 📎 Attachments -->
            <div v-if="item.attachments && item.attachments.length" class="mt-4">
                <h4 class="text-xs sm:text-sm font-semibold text-gray-700 mb-2">📎 Attached Files:</h4>
                <ul class="space-y-1">
                    <li v-for="(file, idx) in item.attachments" :key="idx"
                        class="flex items-center space-x-2 text-xs sm:text-sm break-all">
                        <a @click.stop :href="getUrl(file.file_path)" target="_blank"
                            class="text-blue-600 hover:underline flex items-center gap-2">
                            <font-awesome-icon :icon="['fas', 'file-alt']" class="text-gray-500" />
                            <span>{{ file.file_name || 'Document' }}</span>
                        </a>
                    </li>
                </ul>
            </div>
            <div v-else class="text-xs sm:text-sm text-gray-400 italic mt-4">No files uploaded.</div>
            <div class="absolute bottom-2 right-4">
                <img :src="entityLogo" :alt="item.user.entity.split(',')[0]"
                    class="w-8 h-8 sm:w-10 sm:h-10 object-contain rounded border bg-white shadow" />
            </div>
        </div>

        <hr v-if="item.likes_count || item.comments_count" class="border-t my-2" :class="isModal ? 'mx-3' : ''">

        <div @click.stop="openComment" class="flex flex-col" :class="isModal ? 'px-3' : 'px-4 sm:px-5 md:px-6'">
            <div class="flex justify-between items-center">
                <div>
                    <div v-if="item.likes_count" class="flex items-center gap-2">
                        <img class="ht-16 sm:ht-18" src="../../../assets/images/default/like.svg" alt="">
                        <span class="text-xs sm:text-sm text-gray-400">{{ getLikeText }}</span>
                    </div>
                </div>
                <div>
                    <span v-if="item.comments_count" class="text-xs sm:text-sm text-gray-400">{{ item.comments_count }}
                        {{ item.comments_count > 1 ? 'comments' : 'comment' }}</span>
                </div>
            </div>
            <hr class="border-t my-2">
            <div class="w-full flex flex-row pb-2">
                <div @click.stop="toggleLike"
                    class="w-1/2 flex justify-center items-center gap-2 rounded-md cursor-pointer hover:bg-gray-100 transition">
                    <component :is="item.liked_by_me ? 'IconHandThumbUpSolid' : 'IconHandThumbUpRegular'"
                        :class="['h-5 sm:h-6', item.liked_by_me ? 'text-blue-500' : 'text-gray-400']" />
                    <span class="text-sm sm:text-base" :class="item.liked_by_me ? 'text-blue-500' : 'text-gray-400'">
                        Like
                    </span>
                </div>
                <div @click.stop="openComment"
                    class="w-1/2 flex justify-center items-center gap-2 rounded-md cursor-pointer hover:bg-gray-100 transition">
                    <IconChatBubbleOvalRight class="h-5 sm:h-6 text-gray-400" />
                    <span class="text-gray-400 text-sm sm:text-base">Comment</span>
                </div>
            </div>
        </div>

        <hr v-if="isModal" class="border-t mx-3">

    </div>
</template>

<script>
import { timeAgo } from "../../../helpers/utils";
import { playLikeSound, areSoundsEnabled } from "../../../helpers/soundUtils";
import IconHandThumbUpRegular from "../../../components/icons/IconHandThumbUpRegular.vue";
import IconHandThumbUpSolid from "../../../components/icons/IconHandThumbUpSolid.vue";
import IconChatBubbleOvalRight from "../../../components/icons/IconChatBubbleOvalRight.vue";
import FCHSLogo from '@/assets/images/dashboard/alumni/FCHS.png';
import ADPOLYLogo from '@/assets/images/dashboard/alumni/ADPOLY.png';
import ADVETILogo from '@/assets/images/dashboard/alumni/ADVETI.png';
import ATSLogo from '@/assets/images/dashboard/alumni/ATS.png';
import ThinkTankComments from "./ThinkTankComment.vue";
export default {
    name: "ThinkTankPost",
    props: ["item", "paginatorInfo", "index", "currentDateTime", "isModal"],
    components: {
        IconHandThumbUpRegular,
        IconHandThumbUpSolid,
        IconChatBubbleOvalRight,
        ThinkTankComments,
    },
    computed: {
        profilePicture() {
            return process.env.VUE_APP_PUBLIC_URL + this.item.user.profile_picture_url;
        },
        postedAt() {
            return timeAgo(this.item.created_at, this.currentDateTime);
        },
        entityLogo() {
            const logos = {
                FCHS: FCHSLogo,
                ADPOLY: ADPOLYLogo,
                ADVETI: ADVETILogo,
                ATS: ATSLogo,
                ATHS: ATSLogo,
            };
            return logos[this.item.user.entity.split(',')[0]] || '';
        },
        getLikeText() {
            const likesCount = this.item.likes_count;
            const likedByMe = this.item.liked_by_me;

            if (!likesCount) return "";

            if (likedByMe) {
                return likesCount > 1 ? `You and ${likesCount - 1} others` : "You";
            }

            return String(likesCount);
        },
    },
    methods: {
        getUrl(url) {
            return process.env.VUE_APP_PUBLIC_URL + url;
        },
        toggleLike() {
            // Play like sound if sounds are enabled
            if (areSoundsEnabled()) {
                const soundType = this.item.liked_by_me ? 'unlike' : 'like';
                playLikeSound(soundType);
            }

            // Emit the toggle event
            this.$emit('toggle-like', this.item.token, this.index);
        },
        openComment() {
            this.$emit('open-comment');
        },
        viewItem() {
            if (!this.isModal) {
                this.$emit('view-item', this.item);
            }
        },
        goToProfile() {
            this.$router.push(`/profile/${this.item.user.token}`);
        },
    },
}
</script>

<style lang="scss" scoped>
@responsive {
    .ht-18 {
        height: 18px;
    }

    .ht-16 {
        height: 16px;
    }
}
</style>